import React, { useState, useEffect, useRef, Fragment } from 'react';
import styled from '@emotion/styled';
import { css } from 'twin.macro';
import { connect } from 'react-redux';
import { BsEye } from 'react-icons/bs';
import { FaCircle } from 'react-icons/fa';

import Select from 'react-select';
import { Spinner } from 'react-bootstrap';
import { BootstrapToolTip } from '../../../../_elements/ToolTip';

//import components
import { AdminTable, ActionStyles } from '../../../Admin/reusableStyles';
import { debounce, getDateTimeFromDateTime, toastWrapper } from '../../../../_helpers';
import PreviewEventModal from '../../../Admin/Tasks/PreviewEventModal';
import AdminSearchBox from '../../../Admin/reusableComponents/AdminSearchBox';
import LoadingSpiner from '../../../../_elements/LoadingSpiner';
import Pagination from '../../../Admin/Pagination';
import { externalCalendarEventApi } from '../../../../_services/api/calendarIntegration/externalCalendarEvents';
import CopiedActivityLibraryEventsModal from './CopiedActivityLibraryEventsModal';
import BulkCopyExternalEventsToActivityLibraryModal from './BulkCopyExternalEventsToActivityLibraryModal';
import { Checkbox } from '@/_elements/FormElements';

export const EventPackageTable = styled(AdminTable)``;

const ExternalCalendarEventsLibrary = ({ CurrUser, teamOptions }) => {
    const [showModal, setShowModal] = useState(null);
    const [events, setEvents] = useState([]);
    const [loadingEvent, setLoadingEvent] = useState(false);

    const [searchText, setSearchText] = useState('');
    const inputRef = useRef();
    const [pageLimit, setPageLimit] = useState(10);
    const [calendarProvider, setCalendarProvider] = useState('all');
    const [selectedEventIds, setSelectedEventIds] = useState([]);

    const [togglingVisibilityIds, setTogglingVisibilityIds] = useState([]);
    const limitOptions = [
        { value: 10, label: '10' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
        { value: 1000, label: '1000' },
    ];
    const providerOptions = [
        { value: 'all', label: 'All' },
        { value: 'GOOGLE_CALENDAR', label: 'Google' },
        { value: 'OUTLOOK_CALENDAR', label: 'Outlook' },
    ];

    const onSearch = (val) => {
        if (loadingEvent) return;
        setSearchText(val);
    };

    const handleFetchTask = async (id) => {
        if (!id) {
            return;
        }
        const response = await externalCalendarEventApi.getEvent(id);
        setEvents((prev) => prev.map((task) => (task._id === id ? { ...task, ...response } : task)));
    };

    const handleUpdateTask = (eventId, updatedResponse = {}) => {
        setEvents((prev) => prev.map((task) => (task._id === eventId ? { ...task, ...updatedResponse } : task)));
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const getEvents = async (page = 1, limit = pageLimit, provider = calendarProvider, searchText = '') => {
        try {
            setLoadingEvent(true);
            setCurrentPage(page);
            const res = await externalCalendarEventApi.getEvents({
                page,
                limit,
                calendarProvider: provider,
                searchText,
            });
            const results = res?.results || [];
            setEvents(results);
            setTotalRecords(res?.totalResults || 0);
            setLoadingEvent(false);
        } catch (error) {
            setLoadingEvent(false);
            if (error.name === 'AbortError') {
                return;
            }
            toastWrapper.error(error.message || 'Failed to load External Events');
        }
    };

    const debouncedSearchRef = useRef();
    const hasMountedRef = useRef(false);

    useEffect(() => {
        debouncedSearchRef.current = debounce((nextSearchText) => {
            getEvents(1, pageLimit, calendarProvider, nextSearchText);
        }, 500);
    }, [pageLimit, calendarProvider]);

    useEffect(() => {
        getEvents(1, pageLimit, calendarProvider, searchText);
    }, [pageLimit, calendarProvider]);

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        if (debouncedSearchRef.current) {
            debouncedSearchRef.current(searchText);
        }
    }, [searchText]);

    const handleShowEvent = (id) => {
        setShowModal(id);
    };

    const handleClose = () => {
        setShowModal(null);
    };

    useEffect(() => {
        setSelectedEventIds((prev) =>
            prev.filter((id) => events.some((eventData) => (eventData?._id || eventData?.id) === id))
        );
    }, [events]);

    const isAllVisibleSelected =
        events?.length > 0 && events.every((eventData) => selectedEventIds.includes(eventData?._id || eventData?.id));

    const handleToggleEventSelection = (eventId) => {
        setSelectedEventIds((prev) =>
            prev.includes(eventId) ? prev.filter((selectedId) => selectedId !== eventId) : [...prev, eventId]
        );
    };

    const handleSelectAllVisible = () => {
        if (isAllVisibleSelected) {
            setSelectedEventIds((prev) =>
                prev.filter(
                    (selectedId) => !events.some((eventData) => (eventData?._id || eventData?.id) === selectedId)
                )
            );
            return;
        }
        setSelectedEventIds((prev) =>
            Array.from(
                new Set([...prev, ...events.map((eventData) => eventData?._id || eventData?.id).filter(Boolean)])
            )
        );
    };

    const handleToggleHidden = async (eventData) => {
        const eventId = eventData?._id || eventData?.id;
        if (!eventId) {
            return;
        }
        try {
            setTogglingVisibilityIds((prev) => [...new Set([...prev, eventId])]);
            const updated = await externalCalendarEventApi.updateVisibility(eventId, !eventData?.hideFromCalendar);
            setEvents((prev) =>
                prev.map((eventItem) =>
                    (eventItem?._id || eventItem?.id) === eventId
                        ? { ...eventItem, hideFromCalendar: updated?.hideFromCalendar }
                        : eventItem
                )
            );
            toastWrapper.success(
                updated?.hideFromCalendar ? 'Event hidden from calendar' : 'Event visible in calendar'
            );
        } catch (error) {
            toastWrapper.error(error?.message || 'Failed to update event visibility');
        } finally {
            setTogglingVisibilityIds((prev) => prev.filter((id) => id !== eventId));
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between mb-4 align-items-center">
                <div className="d-flex align-items-center">
                    <AdminSearchBox search={searchText} onSearch={onSearch} placeholder="Search" inputRef={inputRef} />

                    <div className="ml-3 d-flex align-items-center">
                        <span className="mr-2">Calendar:</span>
                        <Select
                            value={providerOptions.find((opt) => opt.value === calendarProvider)}
                            onChange={(option) => setCalendarProvider(option?.value ?? 'all')}
                            options={providerOptions}
                            isSearchable={false}
                            classNamePrefix="react-select"
                            styles={{
                                container: (base) => ({ ...base, width: 120 }),
                                menu: (base) => ({ ...base, zIndex: 5 }),
                            }}
                            isDisabled={loadingEvent}
                        />
                    </div>
                    <div className="ml-3 d-flex align-items-center">
                        <span className="mr-2">Results/Page:</span>
                        <Select
                            value={limitOptions.find((opt) => opt.value === pageLimit)}
                            onChange={(option) => setPageLimit(option?.value ?? 50)}
                            options={limitOptions}
                            isSearchable={false}
                            classNamePrefix="react-select"
                            styles={{
                                container: (base) => ({ ...base, width: 100 }),
                                menu: (base) => ({ ...base, zIndex: 5 }),
                            }}
                            isDisabled={loadingEvent}
                        />
                    </div>
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
                <BulkCopyExternalEventsToActivityLibraryModal
                    selectedEventIds={selectedEventIds}
                    setSelectedEventIds={setSelectedEventIds}
                    onBulkCopy={async () => {
                        await getEvents(currentPage, pageLimit, calendarProvider, searchText);
                    }}
                    currUser={CurrUser}
                    teamOptions={teamOptions}
                />
            </div>
            {/* <p className="text-muted">
                #<i>Note: These are outlook/google events from Personal Calendar(profile), not events of this team</i>
            </p> */}
            <div className="border">
                <EventPackageTable className="w-100">
                    <thead>
                        <tr>
                            <th className="text-center">
                                <input
                                    type="checkbox"
                                    checked={isAllVisibleSelected}
                                    onChange={handleSelectAllVisible}
                                    disabled={!events?.length || loadingEvent}
                                    style={{ height: '20px', width: '20px', cursor: 'pointer' }}
                                />
                            </th>
                            <th>S.N</th>
                            <th>Event Name</th>
                            <th>Start Time</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    {loadingEvent ? (
                        <tbody>
                            <td></td>
                            <td></td>
                            <td className="text-center">
                                <LoadingSpiner size={42} height={200} />
                            </td>
                            <td></td>
                            <td></td>
                        </tbody>
                    ) : events?.length > 0 ? (
                        <Fragment>
                            <tbody>
                                {events.map((eventData, index) => {
                                    const eventTypeDetails = eventData?.eventType || {};
                                    return (
                                        <tr key={`${eventData._id}_${index}`}>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEventIds.includes(eventData?._id || eventData?.id)}
                                                    onChange={() =>
                                                        handleToggleEventSelection(eventData?._id || eventData?.id)
                                                    }
                                                    style={{ height: '20px', width: '20px', cursor: 'pointer' }}
                                                />
                                            </td>
                                            <td>{(currentPage - 1) * pageLimit + (index + 1)}</td>
                                            <td>
                                                <div
                                                    css={css`
                                                        display: flex;
                                                        flex-direction: row;
                                                    `}
                                                >
                                                    <img
                                                        src={eventTypeDetails?.image}
                                                        width="24"
                                                        height="24"
                                                        alt={eventTypeDetails?.title}
                                                        className="mr-1 mt-1"
                                                    />
                                                    {eventData.title}
                                                </div>
                                            </td>
                                            <td>
                                                {eventData?.date ? getDateTimeFromDateTime(eventData.date, false) : '-'}
                                            </td>
                                            <td>
                                                <ActionStyles className="mt-2">
                                                    <div>
                                                        <BootstrapToolTip
                                                            text={'View'}
                                                            cssStyles={
                                                                'top:-70% !important; min-width: fit-content !important;'
                                                            }
                                                        >
                                                            <BsEye
                                                                onClick={() => handleShowEvent(eventData._id)}
                                                                className="icon view"
                                                            />
                                                        </BootstrapToolTip>
                                                    </div>

                                                    <div>
                                                        <CopiedActivityLibraryEventsModal
                                                            externalCalendarEvent={eventData}
                                                        />
                                                    </div>

                                                    <div>
                                                        <BootstrapToolTip
                                                            text={
                                                                eventData?.hideFromCalendar
                                                                    ? 'Toggle to Show on calendar'
                                                                    : 'Toggle to Hide from calendar'
                                                            }
                                                            cssStyles={
                                                                'top:-90% !important; min-width: 230px !important;'
                                                            }
                                                            key={togglingVisibilityIds}
                                                        >
                                                            {togglingVisibilityIds.includes(
                                                                eventData?._id || eventData?.id
                                                            ) ? (
                                                                <Spinner
                                                                    animation="border"
                                                                    role="status"
                                                                    className="text-secondary"
                                                                />
                                                            ) : (
                                                                <div className="icon ">
                                                                    <Checkbox
                                                                        value={!!!eventData?.hideFromCalendar}
                                                                        onChange={(ee) => {
                                                                            handleToggleHidden(eventData);
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </BootstrapToolTip>
                                                    </div>
                                                </ActionStyles>
                                            </td>
                                            {showModal === eventData._id ? (
                                                <PreviewEventModal
                                                    data={eventData}
                                                    handleClose={handleClose}
                                                    handleUpdateTask={handleUpdateTask}
                                                />
                                            ) : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Fragment>
                    ) : (
                        <tbody>
                            <td></td>
                            <td></td>
                            <td className="text-center">No External Events Found</td>
                            <td></td>
                            <td></td>
                        </tbody>
                    )}
                </EventPackageTable>
            </div>
            {totalRecords ? (
                <div className="p-4">
                    <Pagination
                        pageNo={currentPage}
                        totalRecords={totalRecords}
                        pageLimit={pageLimit}
                        pageNeighbours={1}
                        onPageChanged={async (data) => {
                            if (data.currentPage && data.currentPage !== currentPage) {
                                await getEvents(data.currentPage, pageLimit, calendarProvider, searchText);
                            }
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
};

const mapStateToProps = (state) => ({
    rooms: state.rooms.teamRooms,
    CurrUser: state.user.user,
});

export default connect(mapStateToProps, {})(ExternalCalendarEventsLibrary);
